import {
  Badge,
  Box,
  Button,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import React, { useState } from "react";

interface AdCampaign {
  id: number;
  campaignName: string;
  adsetName: string;
  adName: string;
  platform: string;
  status: string;
  clicks: number;
  impressions: number;
  adSpend: number;
  firstClick: string | null;
  ctr: string;
}

interface AdsTabProps {
  campaigns: AdCampaign[];
  pageSize?: number;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "Converted":
      return "green";
    case "Engaged":
      return "blue";
    case "Viewed":
      return "yellow";
    case "Impression Only":
      return "gray";
    default:
      return "gray";
  }
};

const getPlatformColor = (platform: string) => {
  return platform === "FB" ? "blue" : "purple";
};

export const AdsTab: React.FC<AdsTabProps> = ({ campaigns, pageSize = 5 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(campaigns.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCampaigns = campaigns.slice(startIndex, endIndex);

  const totalClicks = campaigns.reduce((sum, campaign) => sum + campaign.clicks, 0);
  const totalImpressions = campaigns.reduce((sum, campaign) => sum + campaign.impressions, 0);
  const totalAdSpend = campaigns.reduce((sum, campaign) => sum + campaign.adSpend, 0);
  const totalConversions = campaigns.filter(c => c.status === "Converted").length;

  return (
    <VStack align="stretch" gap={6}>
      {/* Ads Overview */}
      <Box
        bg="indigo.50"
        border="2px"
        borderColor="indigo.200"
        rounded="xl"
        p={6}
      >
        <HStack gap={4} mb={4}>
          <Box fontSize="2xl">📱</Box>
          <Box>
            <Text fontSize="xl" fontWeight="bold" color="indigo.800">
              Ad Campaign History
            </Text>
            <Text fontSize="sm" color="indigo.600">
              Customer interaction with advertising campaigns
            </Text>
          </Box>
        </HStack>

        <SimpleGrid columns={4} gap={4} mt={4}>
          <Box
            bg="white"
            p={4}
            rounded="lg"
            border="1px"
            borderColor="indigo.100"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Total Campaigns
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="indigo.600">
              {campaigns.length}
            </Text>
          </Box>
          <Box
            bg="white"
            p={4}
            rounded="lg"
            border="1px"
            borderColor="indigo.100"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Total Clicks
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {totalClicks}
            </Text>
          </Box>
          <Box
            bg="white"
            p={4}
            rounded="lg"
            border="1px"
            borderColor="indigo.100"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Total Impressions
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {totalImpressions}
            </Text>
          </Box>
          <Box
            bg="white"
            p={4}
            rounded="lg"
            border="1px"
            borderColor="indigo.100"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Total Ad Spend
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="purple.600">
              ${totalAdSpend.toFixed(2)}
            </Text>
          </Box>
        </SimpleGrid>
      </Box>

      {/* Campaign Timeline with Pagination */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Text fontSize="lg" fontWeight="bold">
            Campaign Interactions
          </Text>
          <HStack gap={2}>
            <Text fontSize="sm" color="gray.600">
              Page {currentPage} of {totalPages}
            </Text>
          </HStack>
        </HStack>

        <VStack align="stretch" gap={3}>
          {paginatedCampaigns.map((campaign) => (
            <Box
              key={campaign.id}
              bg="white"
              p={4}
              rounded="lg"
              border="1px"
              borderColor="gray.200"
              _hover={{
                shadow: "md",
                borderColor: "indigo.300"
              }}
              transition="all 0.2s"
            >
              <VStack align="stretch" gap={3}>
                {/* Header Row */}
                <HStack justify="space-between">
                  <HStack gap={3}>
                    <Badge
                      colorScheme={getStatusColor(campaign.status)}
                      size="sm"
                    >
                      {campaign.status}
                    </Badge>
                    <Badge
                      colorScheme={getPlatformColor(campaign.platform)}
                      variant="solid"
                      size="sm"
                    >
                      {campaign.platform}
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    CTR: {campaign.ctr}
                  </Text>
                </HStack>

                {/* Campaign Details */}
                <VStack align="start" gap={1}>
                  <HStack gap={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Campaign:
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {campaign.campaignName}
                    </Text>
                  </HStack>
                  <HStack gap={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Ad Set:
                    </Text>
                    <Text fontSize="sm">{campaign.adsetName}</Text>
                  </HStack>
                  <HStack gap={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Ad:
                    </Text>
                    <Text fontSize="sm">{campaign.adName}</Text>
                  </HStack>
                </VStack>

                {/* Metrics Row */}
                <SimpleGrid
                  columns={3}
                  gap={4}
                  pt={2}
                  borderTop="1px"
                  borderColor="gray.100"
                >
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Clicks / Impressions
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {campaign.clicks} / {campaign.impressions}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Ad Spend
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      ${campaign.adSpend.toFixed(2)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      First Click
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {campaign.firstClick || "No clicks"}
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </Box>
          ))}
        </VStack>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <HStack justify="center" mt={6} gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <HStack gap={1}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <Button
                  key={pageNum}
                  size="sm"
                  variant={currentPage === pageNum ? "solid" : "outline"}
                  colorScheme={currentPage === pageNum ? "blue" : "gray"}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              ))}
            </HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </HStack>
        )}
      </Box>

      {/* Attribution Summary */}
      <Box
        bg="green.50"
        border="1px"
        borderColor="green.200"
        rounded="lg"
        p={4}
      >
        <HStack gap={3}>
          <Box fontSize="xl">🎯</Box>
          <Box>
            <Text fontWeight="bold" color="green.800">
              Attribution Summary
            </Text>
            <Text fontSize="sm" color="green.700" mt={1}>
              This customer has interacted with {campaigns.length} campaigns 
              across Facebook and Instagram. Total spend: ${totalAdSpend.toFixed(2)} 
              with {totalConversions} conversions.
            </Text>
          </Box>
        </HStack>
      </Box>
    </VStack>
  );
};